import React, { useEffect, useMemo, useState } from 'react';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import type { SnackbarVariant } from '../../components/Snackbar';
import { RESEARCH_DESK_NOTE_CATEGORIES } from './researchDeskCategories';
import FloatingNewNoteButton from './FloatingNewNoteButton';
import ResearchNoteModal from './ResearchNoteModal';

export interface SummaryNewNoteProps {
  selectedStock: string;
  isDarkMode: boolean;
  authToken?: string | null;
  authUserId?: number | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
}

const stripHtmlToText = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const extractTitleFromContent = (html: string): string => {
  if (!html) return '';
  const text = stripHtmlToText(html);
  if (!text) return '';
  return text.split(/\s+/).slice(0, 5).join(' ');
};

const SummaryNewNote: React.FC<SummaryNewNoteProps> = React.memo(
  ({ selectedStock, isDarkMode, authToken, authUserId, onUnauthorized, onNotify }) => {
    const NOTE_CATEGORIES = useMemo(() => [...RESEARCH_DESK_NOTE_CATEGORIES], []);

    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [noteContent, setNoteContent] = useState('');
    const [noteTitle, setNoteTitle] = useState('');
    const [noteCategory, setNoteCategory] = useState('Summary');

    const hasAuthToken = !!authToken;
    const resolvedUserId =
      typeof authUserId === 'number' && Number.isFinite(authUserId) && authUserId > 0
        ? authUserId
        : null;

    const hasNoteText = stripHtmlToText(noteContent).length > 0;
    const derivedTitle = extractTitleFromContent(noteContent);
    const effectiveCategory = noteCategory.trim() || 'Summary';
    const canSubmit = hasNoteText;

    useEffect(() => {
      setNoteTitle(derivedTitle);
    }, [derivedTitle]);

    const openNewNote = () => {
      setNoteCategory('Summary');
      setNoteTitle('');
      setNoteContent('');
      setIsNoteModalOpen(true);
    };

    const submitNewNote = async () => {
      if (!hasAuthToken) {
        if (onNotify) {
          onNotify('Please login to save notes.', 'error');
        }
        return;
      }

      const token = authToken as string;
      const symbol = (selectedStock ?? '').trim();
      if (!symbol) {
        return;
      }
      if (!hasNoteText) {
        if (onNotify) {
          onNotify('Please add some text.', 'error');
        }
        return;
      }

      const title = derivedTitle || 'New Note';
      const associatedWith = effectiveCategory;

      try {
        await adminApi.createResearchDeskNote(token, {
          userId: resolvedUserId ?? undefined,
          title,
          associatedWith,
          stockSymbol: symbol,
          html: noteContent ?? '',
        });

        setIsNoteModalOpen(false);
        setNoteTitle('');
        setNoteContent('');
        setNoteCategory('Summary');
        if (onNotify) {
          onNotify(`${symbol} • Summary • Saved`, 'success');
        }
      } catch (e: any) {
        if (isUnauthorized(e)) {
          onUnauthorized?.();
          return;
        }
        if (onNotify) {
          onNotify(e?.message || 'Failed saving note', 'error');
        }
      }
    };

    return (
      <>
        <FloatingNewNoteButton
          visible={true}
          onClick={openNewNote}
          label="New Note"
          backgroundColor={isDarkMode ? '#374151' : undefined}
        />

        <ResearchNoteModal
          isOpen={isNoteModalOpen}
          onRequestClose={() => setIsNoteModalOpen(false)}
          isDarkMode={isDarkMode}
          noteTitle={noteTitle}
          onChangeTitle={setNoteTitle}
          noteCategory={noteCategory}
          onChangeCategory={setNoteCategory}
          noteContent={noteContent}
          onChangeContent={setNoteContent}
          categories={NOTE_CATEGORIES}
          canSubmit={canSubmit}
          onSubmit={submitNewNote}
        />
      </>
    );
  }
);

SummaryNewNote.displayName = 'SummaryNewNote';

export default SummaryNewNote;
